<template>
  <NavBar />
  <v-container fluid style="margin-top: 185px;">
    <v-row>
      <v-col cols="12">
        <h1 class="text-h4 mb-4">{{ t.profile.title }}</h1>
      </v-col>
    </v-row>

    <v-row>
      <!-- Profile Card -->
      <v-col cols="12" md="4">
        <v-card>
          <v-card-title class="bg-primary text-white">
            <v-icon class="mr-2">mdi-account-circle</v-icon>
            {{ t.profile.account_info }}
          </v-card-title>
          <v-card-text class="pa-4">
            <div class="text-center mb-4">
              <div class="d-inline-block position-relative" style="cursor: pointer;" @click="$refs.avatarInput.click()">
                <v-avatar size="100" :color="user?.avatar ? undefined : 'primary'">
                  <v-img v-if="user?.avatar" :src="user.avatar" cover />
                  <v-icon v-else size="60" color="white">mdi-account</v-icon>
                </v-avatar>
                <v-avatar
                  size="30"
                  color="white"
                  class="position-absolute"
                  style="bottom: 0; right: 0; border: 2px solid #1976D2;"
                >
                  <v-icon size="18" color="primary">mdi-camera</v-icon>
                </v-avatar>
              </div>
              <input
                ref="avatarInput"
                type="file"
                accept="image/*"
                style="display: none;"
                @change="uploadAvatar"
              />
              <div v-if="uploadingAvatar" class="mt-2">
                <v-progress-circular indeterminate size="20" width="2" color="primary" />
              </div>
            </div>
            <v-list density="compact">
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-email</v-icon>
                </template>
                <v-list-item-subtitle>{{ t.common.email }}</v-list-item-subtitle>
                <v-list-item-title>{{ user?.email || '-' }}</v-list-item-title>
              </v-list-item>
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-account</v-icon>
                </template>
                <v-list-item-subtitle>{{ t.common.name }}</v-list-item-subtitle>
                <v-list-item-title>{{ user?.name || '-' }}</v-list-item-title>
              </v-list-item>
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-phone</v-icon>
                </template>
                <v-list-item-subtitle>{{ t.common.phone }}</v-list-item-subtitle>
                <v-list-item-title>{{ user?.phone || '-' }}</v-list-item-title>
              </v-list-item>
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-domain</v-icon>
                </template>
                <v-list-item-subtitle>{{ t.users.client }}</v-list-item-subtitle>
                <v-list-item-title>{{ user?.client_name || user?.client?.name || user?.userClient?.name || '-' }}</v-list-item-title>
              </v-list-item>
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-tag</v-icon>
                </template>
                <v-list-item-subtitle>{{ t.profile.client_type }}</v-list-item-subtitle>
                <v-list-item-title>
                  <v-chip size="small" color="primary" variant="outlined">
                    {{ user?.client?.client_type_name || user?.userClient?.client_type_name || '-' }}
                  </v-chip>
                </v-list-item-title>
              </v-list-item>
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon :color="(user?.client?.is_distributor || user?.userClient?.is_distributor) ? 'success' : 'grey'">
                    {{ (user?.client?.is_distributor || user?.userClient?.is_distributor) ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                </template>
                <v-list-item-subtitle>{{ t.profile.distributor }}</v-list-item-subtitle>
                <v-list-item-title>{{ (user?.client?.is_distributor || user?.userClient?.is_distributor) ? t.common.yes : t.common.no }}</v-list-item-title>
              </v-list-item>
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-calendar</v-icon>
                </template>
                <v-list-item-subtitle>{{ t.profile.member_since }}</v-list-item-subtitle>
                <v-list-item-title>{{ formatDate(user?.created_at) }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>

        <!-- Roles Card -->
        <v-card class="mt-4">
          <v-card-title class="bg-deep-purple text-white d-flex align-center justify-space-between">
            <div>
              <v-icon class="mr-2">mdi-shield-account</v-icon>
              {{ t.users.roles }}
            </div>
            <v-btn icon size="small" variant="text" color="white" @click="loadUserRoles" :loading="loadingRoles">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-4">
            <v-progress-circular v-if="loadingRoles" indeterminate size="24" class="mr-2" />
            <div v-else-if="userRoles.length > 0" class="d-flex flex-wrap ga-2">
              <v-chip
                v-for="role in userRoles"
                :key="role.id"
                :color="role.active ? 'success' : 'grey-lighten-1'"
                :variant="role.active ? 'flat' : 'outlined'"
                size="small"
              >
                <v-icon start size="small">{{ role.active ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
                {{ role.name }}
              </v-chip>
            </div>
            <div v-else class="text-caption text-grey">{{ t.profile.no_roles_loaded }}</div>
          </v-card-text>
        </v-card>

        <!-- Referral Info Card -->
        <v-card class="mt-4" v-if="user?.referrer_name">
          <v-card-title class="bg-success text-white">
            <v-icon class="mr-2">mdi-account-multiple-plus</v-icon>
            {{ t.profile.referral }}
          </v-card-title>
          <v-card-text class="pa-4">
            <div class="text-caption text-grey">{{ t.profile.referred_by }}</div>
            <div class="text-body-1 font-weight-bold">{{ user?.referrer_name || 'N/A' }}</div>
          </v-card-text>
        </v-card>

        <!-- Client Details Card (Read-only) -->
        <v-card class="mt-4" v-if="user?.client">
          <v-card-title class="bg-orange-darken-2 text-white">
            <v-icon class="mr-2">mdi-information-outline</v-icon>
            {{ t.profile.client_details }}
          </v-card-title>
          <v-card-text class="pa-4">
            <v-table density="compact">
              <tbody>
                <tr>
                  <td class="font-weight-medium" width="50%">{{ t.profile.client_type }}</td>
                  <td>
                    <v-chip size="small" color="primary">
                      {{ user.client.client_type_name || '-' }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-medium">{{ t.profile.billing_type }}</td>
                  <td>
                    <v-chip size="small" :color="user.client.billing_type === 'prepaid' ? 'warning' : 'info'">
                      {{ user.client.billing_type === 'prepaid' ? t.clients.prepaid : t.clients.postpaid }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-medium">{{ t.common.status }}</td>
                  <td>
                    <v-chip size="small" :color="user.client.client_status_name === 'active' ? 'success' : 'error'">
                      {{ user.client.client_status_name || '-' }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-medium">{{ t.profile.current_credit }}</td>
                  <td class="text-success font-weight-bold">{{ formatCurrency(user.client.current_credit) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">{{ t.profile.current_debit }}</td>
                  <td class="text-error font-weight-bold">{{ formatCurrency(user.client.current_debit) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">{{ t.profile.max_credit }}</td>
                  <td>{{ formatCurrency(user.client.max_credit) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">{{ t.profile.distributor }}</td>
                  <td>
                    <v-icon :color="user.client.is_distributor ? 'success' : 'grey'">
                      {{ user.client.is_distributor ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                    {{ user.client.is_distributor ? t.common.yes : t.common.no }}
                  </td>
                </tr>
                <tr v-if="user.client.mlm_rank_name">
                  <td class="font-weight-medium">{{ t.profile.mlm_rank }}</td>
                  <td>
                    <v-chip size="small" color="purple">
                      {{ user.client.mlm_rank_name }}
                    </v-chip>
                  </td>
                </tr>
                <tr v-if="user.client.personal_volume > 0 || user.client.group_volume > 0">
                  <td class="font-weight-medium">{{ t.profile.personal_volume }}</td>
                  <td>{{ formatCurrency(user.client.personal_volume) }}</td>
                </tr>
                <tr v-if="user.client.group_volume > 0">
                  <td class="font-weight-medium">{{ t.profile.group_volume }}</td>
                  <td>{{ formatCurrency(user.client.group_volume) }}</td>
                </tr>
                <tr v-if="user.client.parent_name">
                  <td class="font-weight-medium">{{ t.profile.parent_client }}</td>
                  <td>{{ user.client.parent_name }}</td>
                </tr>
                <tr v-if="user.client.app_name">
                  <td class="font-weight-medium">{{ t.profile.application }}</td>
                  <td>{{ user.client.app_name }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">{{ t.common.created_at }}</td>
                  <td>{{ formatDate(user.client.created_at) }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Edit Profile Form -->
      <v-col cols="12" md="8">
        <v-card>
          <v-card-title class="bg-info text-white">
            <v-icon class="mr-2">mdi-account-edit</v-icon>
            {{ t.profile.edit_profile }}
          </v-card-title>
          <v-card-text class="pa-4">
            <v-form ref="profileForm" @submit.prevent="updateProfile">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editForm.name"
                    :label="t.profile.full_name"
                    prepend-inner-icon="mdi-account"
                    variant="outlined"
                    :rules="[v => !!v || t.profile.name_required]"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editForm.username"
                    :label="t.common.username"
                    prepend-inner-icon="mdi-at"
                    variant="outlined"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editForm.email"
                    :label="t.common.email"
                    prepend-inner-icon="mdi-email"
                    variant="outlined"
                    type="email"
                    :rules="emailRules"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editForm.phone"
                    :label="t.common.phone"
                    prepend-inner-icon="mdi-phone"
                    variant="outlined"
                  />
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <div class="text-subtitle-1 font-weight-bold mb-3">
                <v-icon class="mr-1">mdi-lock</v-icon>
                {{ t.profile.change_password }}
              </div>

              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editForm.password"
                    :label="t.profile.new_password"
                    prepend-inner-icon="mdi-lock"
                    :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append-inner="showPassword = !showPassword"
                    :type="showPassword ? 'text' : 'password'"
                    variant="outlined"
                    :hint="t.profile.keep_password_hint"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editForm.confirmPassword"
                    :label="t.profile.confirm_new_password"
                    prepend-inner-icon="mdi-lock-check"
                    :type="showPassword ? 'text' : 'password'"
                    variant="outlined"
                    :rules="confirmPasswordRules"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn
              color="grey"
              variant="text"
              @click="resetForm"
            >
              {{ t.common.reset }}
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              @click="updateProfile"
              :loading="saving"
            >
              <v-icon start>mdi-content-save</v-icon>
              {{ t.profile.save_changes }}
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- Edit Client Data -->
        <v-card class="mt-4">
          <v-card-title class="bg-teal text-white">
            <v-icon class="mr-2">mdi-domain</v-icon>
            {{ t.profile.edit_client_data }}
          </v-card-title>
          <v-card-text class="pa-4">
            <v-form ref="clientForm" @submit.prevent="updateClient">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="clientForm.name"
                    :label="t.profile.company_client_name"
                    prepend-inner-icon="mdi-domain"
                    variant="outlined"
                    :rules="[v => !!v || t.profile.name_required]"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="clientForm.short_name"
                    :label="t.clients.short_name"
                    prepend-inner-icon="mdi-tag"
                    variant="outlined"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="clientForm.phone"
                    :label="t.common.phone"
                    prepend-inner-icon="mdi-phone"
                    variant="outlined"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="clientForm.email"
                    :label="t.common.email"
                    prepend-inner-icon="mdi-email"
                    variant="outlined"
                    type="email"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="clientForm.notification_email"
                    :label="t.profile.notification_email"
                    prepend-inner-icon="mdi-bell-outline"
                    variant="outlined"
                    type="email"
                    :hint="t.profile.notification_email_hint"
                    persistent-hint
                  />
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <!-- Logo Upload Section -->
              <div class="text-subtitle-1 font-weight-bold mb-3">
                <v-icon class="mr-1">mdi-image</v-icon>
                {{ t.profile.client_logo }}
              </div>
              <v-row>
                <v-col cols="12" md="6">
                  <div class="d-flex align-center ga-3">
                    <!-- Logo Preview -->
                    <div
                      class="logo-upload-area d-flex align-center justify-center"
                      :style="{
                        width: '120px',
                        height: '60px',
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        background: clientForm.logo_url ? '#1976d2' : '#f5f5f5',
                        cursor: 'pointer'
                      }"
                      @click="$refs.logoInput.click()"
                    >
                      <img
                        v-if="clientForm.logo_url"
                        :src="clientForm.logo_url"
                        alt="Logo"
                        style="max-height: 50px; max-width: 110px; object-fit: contain;"
                      />
                      <v-icon v-else color="grey">mdi-image-plus</v-icon>
                    </div>
                    <input
                      ref="logoInput"
                      type="file"
                      accept="image/*"
                      style="display: none;"
                      @change="uploadLogo"
                    />
                    <div>
                      <v-btn
                        size="small"
                        variant="outlined"
                        color="primary"
                        @click="$refs.logoInput.click()"
                        :loading="uploadingLogo"
                      >
                        <v-icon start>mdi-upload</v-icon>
                        {{ t.profile.upload_logo }}
                      </v-btn>
                      <v-btn
                        v-if="clientForm.logo_url"
                        size="small"
                        variant="text"
                        color="error"
                        class="ml-2"
                        @click="removeLogo"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                      <div class="text-caption text-grey mt-1">{{ t.profile.logo_hint }}</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="clientForm.logo_url"
                    :label="t.profile.logo_url"
                    prepend-inner-icon="mdi-link"
                    variant="outlined"
                    density="compact"
                    :hint="t.profile.logo_url_hint"
                    persistent-hint
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn
              color="grey"
              variant="text"
              @click="resetClientForm"
            >
              {{ t.common.reset }}
            </v-btn>
            <v-btn
              color="teal"
              variant="flat"
              @click="updateClient"
              :loading="savingClient"
            >
              <v-icon start>mdi-content-save</v-icon>
              {{ t.profile.save_client_data }}
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- Quick Stats -->
        <v-row class="mt-4">
          <v-col cols="12" md="4">
            <v-card color="primary" variant="tonal">
              <v-card-text class="text-center">
                <v-icon size="40" color="primary">mdi-shopping</v-icon>
                <div class="text-h5 font-weight-bold mt-2">{{ stats.totalOrders || 0 }}</div>
                <div class="text-caption">{{ t.profile.total_orders }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card color="success" variant="tonal">
              <v-card-text class="text-center">
                <v-icon size="40" color="success">mdi-cash-multiple</v-icon>
                <div class="text-h5 font-weight-bold mt-2">{{ formatCurrency(stats.totalCommissions || 0) }}</div>
                <div class="text-caption">{{ t.profile.commissions_earned }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card color="info" variant="tonal">
              <v-card-text class="text-center">
                <v-icon size="40" color="info">mdi-account-multiple</v-icon>
                <div class="text-h5 font-weight-bold mt-2">{{ stats.totalReferrals || 0 }}</div>
                <div class="text-caption">{{ t.profile.referred_clients }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Delete Account Section -->
        <v-card class="mt-6" v-if="isDefaultAdminL0" color="red-lighten-5" variant="outlined" border="error">
          <v-card-title class="text-red-darken-2">
            <v-icon class="mr-2" color="red-darken-2">mdi-alert-circle</v-icon>
            {{ t.profile.danger_zone }}
          </v-card-title>
          <v-card-text>
            <div class="text-body-2 mb-3">
              {{ t.profile.delete_account_warning }}
            </div>
            <v-btn
              color="red-darken-2"
              variant="outlined"
              @click="showDeleteDialog = true"
            >
              <v-icon start>mdi-delete-forever</v-icon>
              {{ t.profile.delete_account }}
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Delete Account Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="bg-red-darken-2 text-white">
          <v-icon class="mr-2">mdi-alert</v-icon>
          {{ t.profile.confirm_delete_account }}
        </v-card-title>
        <v-card-text class="pa-4">
          <v-alert type="warning" variant="tonal" class="mb-4">
            <div class="font-weight-bold">{{ t.profile.action_irreversible }}</div>
            <ul class="mt-2 text-body-2">
              <li>{{ t.profile.delete_warning_1 }}</li>
              <li>{{ t.profile.delete_warning_2 }}</li>
              <li>{{ t.profile.delete_warning_3 }}</li>
              <li>{{ t.profile.delete_warning_4 }}</li>
            </ul>
          </v-alert>

          <v-textarea
            v-model="deleteReason"
            :label="t.profile.delete_reason"
            variant="outlined"
            rows="3"
            hide-details
            class="mb-4"
          />

          <v-text-field
            v-model="deleteConfirmText"
            :label="t.profile.type_delete_confirm"
            variant="outlined"
            hide-details
            :error="deleteConfirmText.length > 0 && deleteConfirmText !== 'STERGE'"
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn variant="text" @click="cancelDelete">{{ t.common.cancel }}</v-btn>
          <v-spacer />
          <v-btn
            color="red-darken-2"
            variant="flat"
            :disabled="deleteConfirmText !== 'STERGE'"
            :loading="deletingAccount"
            @click="confirmDeleteAccount"
          >
            <v-icon start>mdi-delete-forever</v-icon>
            {{ t.profile.delete_permanently }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import { BASE_URL } from "@/composables/url_config"
import { t as tRef } from "@/composables/useLanguages"
import { deleteClient } from "@/composables/useGetClientsListAPI"
import axios from 'axios'

export default {
  name: 'MyProfileView',
  components: { NavBar },
  data() {
    return {
      user: null,
      loading: false,
      saving: false,
      uploadingAvatar: false,
      showPassword: false,

      editForm: {
        name: '',
        username: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: ''
      },

      clientForm: {
        name: '',
        short_name: '',
        phone: '',
        email: '',
        notification_email: '',
        logo_url: ''
      },
      savingClient: false,
      uploadingLogo: false,

      stats: {
        totalOrders: 0,
        totalCommissions: 0,
        totalReferrals: 0
      },

      // Roles
      userRoles: [],
      loadingRoles: false,

      // Delete account
      showDeleteDialog: false,
      deleteReason: '',
      deleteConfirmText: '',
      deletingAccount: false,

      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',

    }
  },
  computed: {
    t() {
      return tRef.value
    },
    token() {
      return this.$store.getters.getToken
    },
    isDefaultAdminL0() {
      return this.userRoles.some(r => r.name === 'default_admin_l0' && r.active)
    },
    emailRules() {
      return [
        v => !!v || this.t.profile.email_required,
        v => /.+@.+\..+/.test(v) || this.t.profile.email_invalid
      ]
    },
    confirmPasswordRules() {
      return [
        v => !this.editForm.password || v === this.editForm.password || this.t.profile.passwords_mismatch
      ]
    }
  },
  mounted() {
    this.loadUserProfile()
    this.loadStats()
    this.loadUserRoles()
  },
  methods: {
    async loadUserProfile() {
      this.loading = true
      try {
        const response = await axios.get(`${BASE_URL}/home/getLoggedInUserApi`, {
          headers: {
            Authorization: `Bearer ${this.token}`,
            'Content-Type': 'application/json'
          }
        })

        console.log('getLoggedInUserApi response:', response.data)
        if (!response.data.error && response.data.user) {
          this.user = response.data.user
          console.log('User loaded from API:', this.user)
          console.log('Client data:', this.user.client)
          this.populateForm()
        } else {
          console.log('API returned error or no user, falling back to store')
          // Fallback to store user data
          const storeUser = this.$store.getters.getUser
          if (storeUser) {
            this.user = storeUser
            this.populateForm()
          }
        }
      } catch (err) {
        console.error('Error loading profile:', err)
        // Fallback to store user data on error
        const storeUser = this.$store.getters.getUser
        if (storeUser) {
          this.user = storeUser
          this.populateForm()
        } else {
          this.showMessage(this.t.profile.error_loading_profile, 'error')
        }
      } finally {
        this.loading = false
      }
    },

    async loadStats() {
      try {
        // Load commission stats
        const commResponse = await axios.get(`${BASE_URL}/mlm/get_commission_balance_api`, {
          headers: { Authorization: `Bearer ${this.token}` }
        })
        if (!commResponse.data.error && commResponse.data.balance) {
          this.stats.totalCommissions = commResponse.data.balance.total_earned || 0
        }

        // Load referral stats
        const refResponse = await axios.get(`${BASE_URL}/mlm/get_my_referrals_api`, {
          headers: { Authorization: `Bearer ${this.token}` }
        })
        if (!refResponse.data.error && refResponse.data.stats) {
          this.stats.totalReferrals = refResponse.data.stats.total_referrals || 0
        }
      } catch (err) {
        console.error('Error loading stats:', err)
      }
    },

    populateForm() {
      if (this.user) {
        this.editForm.name = this.user.name || ''
        this.editForm.username = this.user.username || ''
        this.editForm.email = this.user.email || ''
        this.editForm.phone = this.user.phone || ''
        this.editForm.password = ''
        this.editForm.confirmPassword = ''
      }
      this.populateClientForm()
    },

    resetForm() {
      this.populateForm()
    },

    async updateProfile() {
      const { valid } = await this.$refs.profileForm.validate()
      if (!valid) return

      if (!this.user) {
        this.showMessage(this.t.profile.user_data_unavailable, 'error')
        return
      }

      if (this.editForm.password && this.editForm.password !== this.editForm.confirmPassword) {
        this.showMessage(this.t.profile.passwords_mismatch, 'error')
        return
      }

      this.saving = true
      try {
        const params = {
          user_id: this.user.id,
          name: this.editForm.name,
          username: this.editForm.username,
          email: this.editForm.email,
          phone: this.editForm.phone
        }

        if (this.editForm.password && this.editForm.password.length >= 6) {
          params.password = this.editForm.password
        }

        const response = await axios.post(`${BASE_URL}/home/update_user_profile_api`, params, {
          headers: {
            Authorization: `Bearer ${this.token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.error) {
          this.showMessage(response.data.message, 'error')
        } else {
          this.showMessage(this.t.profile.profile_updated, 'success')
          this.editForm.password = ''
          this.editForm.confirmPassword = ''
          // Reload user profile and update store
          await this.loadUserProfile()
          if (this.user) {
            this.$store.commit('setUser', { user: this.user })
          }
        }
      } catch (err) {
        console.error('Error updating profile:', err)
        this.showMessage(this.t.profile.error_updating_profile, 'error')
      } finally {
        this.saving = false
      }
    },

    async uploadAvatar(event) {
      const file = event.target.files[0]
      if (!file) return

      this.uploadingAvatar = true
      try {
        const formData = new FormData()
        formData.append('picture', file)

        const response = await axios.post(`${BASE_URL}/home/updateImageProfile`, formData, {
          headers: {
            Authorization: `Bearer ${this.token}`,
            'Content-Type': 'multipart/form-data'
          }
        })

        if (!response.data.error && response.data.avatar) {
          this.user.avatar = response.data.avatar
          this.$store.commit('setUser', { user: { ...this.user } })
          this.showMessage(this.t.profile.avatar_updated, 'success')
        } else {
          this.showMessage(response.data.message || this.t.profile.error_updating_avatar, 'error')
        }
      } catch (err) {
        console.error('Error uploading avatar:', err)
        this.showMessage(this.t.profile.error_uploading_image, 'error')
      } finally {
        this.uploadingAvatar = false
        this.$refs.avatarInput.value = ''
      }
    },

    showMessage(message, color) {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'EUR' }).format(value || 0)
    },

    populateClientForm() {
      // client comes from getLoggedInUserApi, userClient comes from loginWithToken (store)
      const client = this.user?.client || this.user?.userClient
      console.log('populateClientForm - client object:', client)
      if (client) {
        this.clientForm.name = client.name || ''
        this.clientForm.short_name = client.short_name || ''
        this.clientForm.phone = client.phone || ''
        this.clientForm.email = client.email || ''
        this.clientForm.notification_email = client.notification_email || ''
        this.clientForm.logo_url = client.logo_url || ''
        console.log('clientForm populated:', this.clientForm)
      } else if (this.user) {
        // Fallback if no client object available
        this.clientForm.name = this.user.client_name || ''
        this.clientForm.short_name = ''
        this.clientForm.phone = ''
        this.clientForm.email = this.user.email || ''
        this.clientForm.notification_email = ''
        this.clientForm.logo_url = ''
      }
    },

    resetClientForm() {
      this.populateClientForm()
    },

    async updateClient() {
      const { valid } = await this.$refs.clientForm.validate()
      if (!valid) return

      if (!this.user || !this.user.client_id) {
        this.showMessage(this.t.profile.client_data_unavailable, 'error')
        return
      }

      this.savingClient = true
      try {
        const params = {
          client_id: this.user.client_id,
          name: this.clientForm.name,
          short_name: this.clientForm.short_name,
          phone: this.clientForm.phone,
          email: this.clientForm.email,
          notification_email: this.clientForm.notification_email,
          logo_url: this.clientForm.logo_url
        }

        const response = await axios.post(`${BASE_URL}/home/update_client_api`, params, {
          headers: {
            Authorization: `Bearer ${this.token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.error) {
          this.showMessage(response.data.message, 'error')
        } else {
          this.showMessage(this.t.profile.client_updated, 'success')
          await this.loadUserProfile()
        }
      } catch (err) {
        console.error('Error updating client:', err)
        this.showMessage(this.t.profile.error_updating_client, 'error')
      } finally {
        this.savingClient = false
      }
    },

    async uploadLogo(event) {
      const file = event.target.files[0]
      if (!file) return

      if (!this.user || !this.user.client_id) {
        this.showMessage(this.t.profile.client_data_unavailable, 'error')
        return
      }

      this.uploadingLogo = true
      try {
        const formData = new FormData()
        formData.append('logo', file)
        formData.append('client_id', this.user.client_id)

        const response = await axios.post(`${BASE_URL}/home/upload_client_logo_api`, formData, {
          headers: {
            Authorization: `Bearer ${this.token}`,
            'Content-Type': 'multipart/form-data'
          }
        })

        if (!response.data.error && response.data.logo_url) {
          this.clientForm.logo_url = response.data.logo_url
          this.showMessage(this.t.profile.logo_uploaded, 'success')
        } else {
          this.showMessage(response.data.message || this.t.profile.error_uploading_logo, 'error')
        }
      } catch (err) {
        console.error('Error uploading logo:', err)
        this.showMessage(this.t.profile.error_uploading_logo, 'error')
      } finally {
        this.uploadingLogo = false
        this.$refs.logoInput.value = ''
      }
    },

    removeLogo() {
      this.clientForm.logo_url = ''
    },

    async loadUserRoles() {
      this.loadingRoles = true
      try {
        const storeUser = this.$store.getters.getUser
        const userId = this.user?.id || storeUser?.id
        if (!userId) return

        const response = await axios.get(`${BASE_URL}/home/getUserRoles`, {
          headers: { Authorization: `Bearer ${this.token}` },
          params: { user_id: userId }
        })
        if (response.data?.user_roles) {
          this.userRoles = response.data.user_roles
        }
      } catch (err) {
        console.error('Error loading roles:', err)
      } finally {
        this.loadingRoles = false
      }
    },

    cancelDelete() {
      this.showDeleteDialog = false
      this.deleteReason = ''
      this.deleteConfirmText = ''
    },

    async confirmDeleteAccount() {
      if (this.deleteConfirmText !== 'STERGE') return

      this.deletingAccount = true
      try {
        const clientId = this.user?.client_id || this.user?.client?.id
        if (!clientId) {
          this.showMessage(this.t.profile.cannot_identify_client, 'error')
          return
        }

        const result = await deleteClient(this.token, clientId, this.deleteReason || 'User requested account deletion')

        if (result.error) {
          this.showMessage(result.message || this.t.profile.error_deleting_account, 'error')
        } else {
          this.showDeleteDialog = false
          this.showMessage(this.t.profile.account_deleted, 'success')
          setTimeout(() => {
            this.$store.commit('setLoggedIn', false)
            this.$store.commit('setToken', { token: null })
            this.$store.commit('setUser', { user: null })
            sessionStorage.clear()
            this.$router.push('/homeLoggedOut')
          }, 2000)
        }
      } catch (err) {
        console.error('Error deleting account:', err)
        this.showMessage(this.t.profile.error_deleting_account, 'error')
      } finally {
        this.deletingAccount = false
      }
    }
  }
}
</script>
